import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';

import { merchantsApi, shopsApi, trustedCustomersApi } from 'api';
import {
  CloseDialogResult,
  CopyText,
  CrudPage,
  DataGridColumnDefinition,
  dataGridColumns,
  Dialog,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { FilterDefinitionType, QueryKey } from 'enums';
import { useMutation, usePartialQuery, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  TrustedCustomer,
  TrustedCustomersFilters,
} from 'types';
import { formatUtils } from 'utils';

export const TrustedCustomersPage: React.FC = () => {
  const { t: tLayout } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'layout',
  });
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.trusted_customers',
  });
  const { t: tCommon } = useTranslation();
  const { role, isAdmin, isTechOperator } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const queryResult = usePartialQuery(
    QueryKey.TrustedCustomers,
    trustedCustomersApi.getAllPaginated,
  );

  const [confirmRemoveDialogProps, setConfirmRemoveDialogProps] = useState<{
    open: boolean;
    data?: TrustedCustomer;
  }>({
    open: false,
  });

  const queryResultMerchants = useQuery(
    QueryKey.Merchants,
    merchantsApi.getAllAsRole(role),
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const { mutate: removeTrustedCustomer } = useMutation(
    trustedCustomersApi.remove,
    {
      onSuccess: () => {
        setConfirmRemoveDialogProps({ open: false });
        queryClient.invalidateQueries(QueryKey.TrustedCustomers);
      },
    },
  );

  const handleCloseRemoveDialog = useCallback(
    ({ ok, data }: CloseDialogResult<TrustedCustomer>) => {
      if (ok && data) {
        removeTrustedCustomer(data.id);
      } else {
        setConfirmRemoveDialogProps({ open: false });
      }
    },
    [removeTrustedCustomer],
  );

  const detailsUrl = useMemo(() => {
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.TRUSTED_CUSTOMERS_DETAILS;
    }
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.TRUSTED_CUSTOMERS_DETAILS;
    }
    return undefined;
  }, [isAdmin, isTechOperator]);

  const handleCreateOrEdit = useCallback(
    (id: string) => navigate(generatePath(detailsUrl!, { id })),
    [detailsUrl, navigate],
  );

  const getEditLink = useCallback(
    (data: TrustedCustomer) => ({
      to: generatePath(detailsUrl!, { id: data.id }),
    }),
    [detailsUrl],
  );

  const handleRemove = useCallback((data: TrustedCustomer) => {
    setConfirmRemoveDialogProps({ open: true, data });
  }, []);

  const columns = useMemo(
    (): DataGridColumnDefinition<TrustedCustomer>[] => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.shop'),
        valueGetter: (item) => (
          <Fragment>
            <div>{item.merchant?.user?.name}</div>
            <div>{formatUtils.formatName(item.shop)}</div>
          </Fragment>
        ),
      },
      {
        header: t('fields.customers'),
        multiValueRenderer: {
          itemsGetter: (item) => item.customers ?? [],
          valueGetter: ({
            customerId,
            customerIp,
          }: {
            customerId: string;
            customerIp: string;
          }) => (
            <div className="tw-mb-2">
              {customerId && (
                <div>
                  <CopyText text={customerId} />
                </div>
              )}
              {customerIp && (
                <div>
                  <CopyText text={customerIp} />
                </div>
              )}
            </div>
          ),
        },
      },
      dataGridColumns.getActionsColumn({
        handleRemove,
        getEditLink,
      }),
    ],
    [t, handleRemove, getEditLink],
  );

  const filtersDefinitions: FilterDefinition<TrustedCustomersFilters>[] =
    useMemo(
      () => [
        {
          label: tCommon('filters.merchant'),
          name: 'merchantId',
          type: FilterDefinitionType.Merchant,
          merchants: queryResultMerchants.data,
        },
        {
          label: tCommon('filters.shop'),
          name: 'shopId',
          type: FilterDefinitionType.Shop,
          shops: queryResultShops.data,
        },
        {
          label: tCommon('filters.customer'),
          name: 'customerId',
          type: FilterDefinitionType.Text,
        },
      ],
      [queryResultMerchants.data, queryResultShops.data, tCommon],
    );

  return (
    <Fragment>
      <CrudPage
        header={{
          title: tLayout('menu_items.customers'),
          rightContentButton: { onClick: () => handleCreateOrEdit(NEW_ID) },
        }}
        filters={{ filtersDefinitions }}
        table={{ queryResult, columns, paginated: true }}
      >
        <Dialog
          title={t('remove_dialog.title')}
          onClose={handleCloseRemoveDialog}
          {...confirmRemoveDialogProps}
        >
          <Fragment>
            <div className="tw-mb-4">
              {t('remove_dialog.description', {
                count: confirmRemoveDialogProps.data?.customers?.length,
              })}
            </div>
            {confirmRemoveDialogProps.data && (
              <Fragment>
                <div className="tw-mb-2">
                  <div>{`${t('fields.shop')}:`}</div>
                  <div>{confirmRemoveDialogProps.data.merchant.user.name}</div>
                  <div>
                    {formatUtils.formatName(confirmRemoveDialogProps.data.shop)}
                  </div>
                </div>
              </Fragment>
            )}
          </Fragment>
        </Dialog>
      </CrudPage>
    </Fragment>
  );
};
